import {
	getFormProps,
	getInputProps,
	getTextareaProps,
	useForm,
} from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod/v4'
import React, { useEffect, useId, useState } from 'react'
import { Link, useActionData, useFetcher, useLocation } from 'react-router'
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { toast as showToast } from 'sonner'
import CookieConsent from '#app/components/cookie-consent.tsx'
import { ErrorList, Field, TextareaField } from '#app/components/forms.tsx'
import { Button } from '#app/components/ui/button.tsx'
import {
	Dialog,
	DialogClose,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from '#app/components/ui/dialog.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import { StatusButton } from '#app/components/ui/status-button.tsx'
import { useIsPending } from '#app/utils/misc.tsx'
import {
	type ReportIssueActionResponse,
	ReportIssueSchema,
} from '#app/utils/report-issue.ts'
import { useOptionalUser } from '#app/utils/user.ts'

export function ReportIssueDialog() {
	const formAction = '/api/report-issue/'
	const user = useOptionalUser()
	const actionData = useActionData<ReportIssueActionResponse>()
	const isPending = useIsPending({ formAction })
	const location = useLocation()
	const fetcher = useFetcher<ReportIssueActionResponse>()
	const [isOpen, setIsOpen] = useState(false)

	const id = useId()
	const formId = `${id}-${location.pathname}-${user?.id ?? 'guest'}`
	const reportUrl = `${location.pathname}${location.search}${location.hash}`

	const [form, fields] = useForm({
		id: formId,
		constraint: getZodConstraint(ReportIssueSchema),
		defaultValue: {
			name: user?.name,
			email: user?.email,
			reportUrl,
			description: '',
		},
		lastResult: actionData?.result,
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: ReportIssueSchema })
		},
		shouldRevalidate: 'onBlur',
	})

	const [previousResult, setPreviousResult] = useState(fetcher.data)
	if (fetcher.state === 'idle' && fetcher.data !== previousResult) {
		setPreviousResult(fetcher.data)
		if (fetcher.data?.result) setIsOpen(false)
	}

	useEffect(() => {
		if (fetcher.state === 'idle' && fetcher.data?.result) {
			setTimeout(() => {
				showToast.success('Report Issue', {
					description: 'Issue reported successfully! Thank you!',
				})
			}, 0)
		}
	}, [fetcher.state, fetcher.data])

	return (
		<Dialog open={isOpen} onOpenChange={setIsOpen} modal={true}>
			<DialogTrigger asChild>
				<Button size="sm" onClick={() => setIsOpen(true)}>
					Report Issue <Icon className="align ml-2" name="envelope-closed" />
				</Button>
			</DialogTrigger>
			<DialogContent
				className="sm:max-w-[425px]"
				onInteractOutside={(event) => event.preventDefault()}
			>
				<fetcher.Form method="post" action={formAction} {...getFormProps(form)}>
					<DialogHeader>
						<DialogTitle>Report an issue</DialogTitle>
						<DialogDescription>
							Tell us about a problem on this page. Click submit when you're
							done.
						</DialogDescription>
					</DialogHeader>
					<HoneypotInputs />
					<Field
						labelProps={{}}
						inputProps={{
							...getInputProps(fields.reportUrl, { type: 'hidden' }),
						}}
						errors={fields.reportUrl.errors}
					/>
					<Field
						className="mt-8"
						labelProps={{ htmlFor: fields.name.id, children: 'Name' }}
						inputProps={{
							...getInputProps(fields.name, { type: 'text' }),
							autoFocus: true,
						}}
						errors={fields.name.errors}
					/>
					<Field
						labelProps={{ htmlFor: fields.email.id, children: 'Email' }}
						inputProps={{
							...getInputProps(fields.email, { type: 'email' }),
						}}
						errors={fields.email.errors}
					/>
					<ErrorList errors={form.errors} id={form.errorId} />
					<TextareaField
						labelProps={{
							htmlFor: fields.description.id,
							children: 'Description',
						}}
						textareaProps={{
							rows: 5,
							...getTextareaProps(fields.description),
						}}
						errors={fields.description.errors}
					/>
					<DialogFooter>
						<DialogClose asChild>
							<Button type="button" variant="secondary">
								Cancel
							</Button>
						</DialogClose>
						<StatusButton
							status={isPending ? 'pending' : (form.status ?? 'idle')}
							type="submit"
							form={formId}
						>
							Submit
						</StatusButton>
					</DialogFooter>
				</fetcher.Form>
			</DialogContent>
		</Dialog>
	)
}

interface FooterProps {
	className?: string
}

export const Footer: React.FC<FooterProps> = ({ className }) => (
	<footer className={className}>
		<div className="flex flex-wrap items-center justify-center gap-3">
			<span className="flex items-center">
				&copy; {new Date().getFullYear()} {import.meta.env.VITE_APP_TITLE}
			</span>
			<Icon name="divider-vertical" size="md" />
			<Link to="/about" className="flex items-center">
				About
			</Link>
			<Icon name="divider-vertical" size="md" />
			<Link to="/support" className="flex items-center">
				Support
			</Link>
			<Icon name="divider-vertical" size="md" />
			<Link to="/legal-notice" className="flex items-center">
				Legal Notices
			</Link>
			<Icon name="divider-vertical" size="md" />
			<CookieConsent />
			<Icon name="divider-vertical" size="md" />
			<ReportIssueDialog />
		</div>
	</footer>
)
